import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Reorder } from "framer-motion";
import { Plus, Trash2, Upload, Save, ArrowLeft, ImageIcon, Lock, LogOut, GripVertical, CheckCircle2, Inbox, Users } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { saveContent, uploadPhoto, deletePhoto, savePhotoOrder, photoSrc, imgSrc, memberSlug, focusStyle, login, getToken, setToken, clearToken, fetchLeads, updateLead, deleteLead, fetchStats } from "@/lib/api";
import { FaqEditor } from "@/components/admin/FaqEditor";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ICON_OPTIONS = ["HeartPulse", "ShieldCheck", "Car", "PiggyBank", "Umbrella"];

const FIELD_LABELS = { badge: "徽章", line1: "标题第一行", line2: "标题第二行", subtitle: "副标题", cta: "按钮文字", title: "标题", body: "正文", name: "姓名", role: "职务", desc: "描述", quote: "内容", bio: "背景介绍（详细资料）", address: "地址", hours: "服务时间" };

const Field = ({ label, value, onChange, multiline, testid }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">{label}</label>
    {multiline ? (
      <Textarea data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} rows={3} className="bg-white" />
    ) : (
      <Input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="bg-white" />
    )}
  </div>
);

const PositionPicker = ({ src, x, y, onChange, testid }) => {
  const ref = useRef(null);
  const setFromEvent = (e) => {
    const r = ref.current.getBoundingClientRect();
    const px = Math.min(100, Math.max(0, Math.round(((e.clientX - r.left) / r.width) * 100)));
    const py = Math.min(100, Math.max(0, Math.round(((e.clientY - r.top) / r.height) * 100)));
    onChange(px, py);
  };
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">照片显示位置（点击/拖动预览图调整）</label>
      <div
        ref={ref}
        data-testid={testid}
        onMouseDown={(e) => {
          e.preventDefault();
          setFromEvent(e);
          const mv = (ev) => setFromEvent(ev);
          const up = () => {
            window.removeEventListener("mousemove", mv);
            window.removeEventListener("mouseup", up);
          };
          window.addEventListener("mousemove", mv);
          window.addEventListener("mouseup", up);
        }}
        className="relative w-44 aspect-[4/3] rounded-xl overflow-hidden cursor-crosshair border-2 border-slate-200 bg-sky-50"
      >
        <img src={src} alt="" draggable={false} className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
          style={{ objectPosition: `${x}% ${y}%` }} />
        <span className="absolute w-4 h-4 rounded-full bg-orange-500 border-2 border-white shadow-md -translate-x-1/2 -translate-y-1/2 pointer-events-none"
          style={{ left: `${x}%`, top: `${y}%` }} />
      </div>
    </div>
  );
};

export default function Admin() {
  const { content, photos, refresh, loading } = useSite();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [capZh, setCapZh] = useState("");
  const [capEn, setCapEn] = useState("");
  const [authed, setAuthed] = useState(!!getToken());
  const [pwd, setPwd] = useState("");
  const [loginErr, setLoginErr] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [photoList, setPhotoList] = useState([]);
  const photoListRef = useRef([]);
  const fileRef = useRef(null);
  const heroFileRef = useRef(null);
  const avatarFileRef = useRef(null);
  const avatarTargetIdx = useRef(null);

  const onHeroImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const r = await uploadPhoto(file, "", "", "hero");
      set("hero.image", `/api/files/${r.storage_path}`);
      toast.success("图片已上传，点击「保存全部更改」生效 / Uploaded — remember to Save");
    } catch (err) {
      if (!handle401(err)) toast.error("上传失败 / Upload failed");
    } finally {
      if (heroFileRef.current) heroFileRef.current.value = "";
    }
  };

  useEffect(() => {
    if (content && !draft) setDraft(JSON.parse(JSON.stringify(content)));
  }, [content, draft]);

  useEffect(() => {
    setPhotoList(photos);
    photoListRef.current = photos;
  }, [photos]);

  useEffect(() => {
    if (authed) {
      loadLeads();
      loadStats();
    }
  }, [authed]);

  const handle401 = (e) => {
    if (e?.response?.status === 401) {
      clearToken();
      setAuthed(false);
      toast.error("登录已过期，请重新登录 / Session expired");
      return true;
    }
    return false;
  };

  const loadLeads = async () => {
    try {
      setLeads(await fetchLeads());
    } catch (e) {
      handle401(e);
    }
  };

  const loadStats = async () => {
    try {
      setStats(await fetchStats());
    } catch (e) {
      handle401(e);
    }
  };

  const onLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginErr("");
    try {
      const r = await login(pwd);
      setToken(r.token);
      setAuthed(true);
      setPwd("");
      toast.success("登录成功 / Welcome back");
    } catch (err) {
      setLoginErr(err?.response?.status === 429 ? "尝试次数过多，请 15 分钟后再试" : "密码错误 / Wrong password");
    } finally {
      setLoggingIn(false);
    }
  };

  const onLogout = () => {
    clearToken();
    setAuthed(false);
  };

  const peopleEditor = (arrName, label, withBio = false) => (
    <div className="space-y-6">
      <input ref={avatarFileRef} data-testid={`${arrName}-avatar-file-input`} type="file" accept="image/*" className="hidden" onChange={onAvatarUpload} />
      <p className="text-xs font-semibold text-slate-400 flex items-center gap-1.5">
        <GripVertical size={14} /> 拖拽卡片调整{label}顺序，点击「保存全部更改」后生效
      </p>
      <Reorder.Group axis="y" as="div" values={draft?.[arrName] || []} onReorder={(n) => setDraft({ ...draft, [arrName]: n })} className="space-y-6">
        {(draft?.[arrName] || []).map((m, i) => (
          <Reorder.Item key={m.id || i} value={m} as="div" className="cursor-grab active:cursor-grabbing">
            <Card className="rounded-3xl border-slate-200" data-testid={`admin-${arrName}-item-${i}`}>
              <CardHeader className="flex-row items-center justify-between">
                <CardTitle className="font-display flex items-center gap-2"><Users size={18} className="text-sky-500" /> {label} {i + 1}</CardTitle>
                <Button data-testid={`admin-${arrName}-remove-${i}`} variant="ghost" size="sm" onClick={() => removeArrItem(arrName, i)} className="text-red-500 hover:text-red-600">
                  <Trash2 size={15} />
                </Button>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="flex items-center gap-5 flex-wrap">
                  <div className="w-20 h-20 rounded-2xl bg-sky-50 border border-slate-200 overflow-hidden shrink-0 grid place-items-center">
                    {imgSrc(m.avatar) ? (
                      <img src={imgSrc(m.avatar)} alt="" style={focusStyle(m)} className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon size={24} className="text-slate-300" />
                    )}
                  </div>
                  <div className="flex-1 min-w-48 space-y-2">
                    <Button data-testid={`admin-${arrName}-avatar-upload-${i}`} variant="outline" size="sm" className="rounded-full"
                      onClick={() => { avatarTargetIdx.current = { arr: arrName, idx: i }; avatarFileRef.current?.click(); }}>
                      <Upload size={14} className="mr-1.5" /> 上传头像
                    </Button>
                    <Field label="或粘贴头像 URL" testid={`${arrName}-${i}-avatar`} value={m.avatar || ""} onChange={(v) => setArr(arrName, i, "avatar", v)} />
                  </div>
                  {imgSrc(m.avatar) && (
                    <PositionPicker
                      testid={`${arrName}-${i}-position-picker`}
                      src={imgSrc(m.avatar)}
                      x={m.focus_x ?? 50}
                      y={m.focus_y ?? 30}
                      onChange={(px, py) => {
                        const next = JSON.parse(JSON.stringify(draft));
                        next[arrName][i]["focus_x"] = px;
                        next[arrName][i]["focus_y"] = py;
                        setDraft(next);
                      }}
                    />
                  )}
                </div>
                {biFields(`${arrName}-${i}`, m, { arr: arrName, idx: i }, withBio ? ["name", "role", "bio"] : ["name", "role"], withBio ? ["bio"] : [])}
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="「我想加入」按钮链接（留空则不显示）" testid={`${arrName}-${i}-join-url`} value={m.join_url || ""} onChange={(v) => setArr(arrName, i, "join_url", v)} />
                  <Field label="「了解保单」按钮链接（留空则不显示）" testid={`${arrName}-${i}-policy-url`} value={m.policy_url || ""} onChange={(v) => setArr(arrName, i, "policy_url", v)} />
                </div>
                {arrName === "members" && (
                  <div className="space-y-1.5">
                    <Field label="专属链接 Slug（留空自动按英文名生成）" testid={`${arrName}-${i}-slug`} value={m.slug || ""} onChange={(v) => setArr(arrName, i, "slug", v)} />
                    <p className="text-xs text-slate-400">专属网址：/members/{memberSlug(m)}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </Reorder.Item>
        ))}
      </Reorder.Group>
      <Button data-testid={`admin-${arrName}-add`} variant="outline" className="rounded-full" onClick={() => addArrItem(arrName, { id: `${arrName}-${Date.now()}`, name_zh: "", name_en: "", role_zh: "", role_en: "", avatar: "", join_url: "", policy_url: "" })}>
        <Plus size={15} className="mr-1" /> 添加{label}
      </Button>
    </div>
  );

  if (!authed) {
    return (
      <div data-testid="admin-login" className="min-h-screen grid place-items-center bg-slate-50 px-6">
        <form onSubmit={onLogin} className="w-full max-w-sm rounded-3xl bg-white border border-slate-200 p-8 shadow-[0_20px_50px_rgb(0,0,0,0.06)]">
          <div className="w-14 h-14 rounded-2xl bg-sky-400 grid place-items-center text-white mb-6">
            <Lock size={24} />
          </div>
          <h1 className="font-display font-extrabold text-2xl text-slate-900">管理后台登录</h1>
          <p className="mt-2 text-sm text-slate-500">请输入管理口令以编辑网站内容</p>
          <Input
            data-testid="admin-password-input"
            type="password"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            placeholder="管理口令 / Password"
            className="mt-6 bg-white"
            autoFocus
          />
          {loginErr && <p data-testid="admin-login-error" className="mt-3 text-sm font-semibold text-red-500">{loginErr}</p>}
          <Button data-testid="admin-login-button" type="submit" disabled={loggingIn || !pwd} className="mt-6 w-full rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold">
            {loggingIn ? "登录中…" : "登录"}
          </Button>
          <a href="/" className="mt-4 flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-sky-500 transition-colors duration-300">
            <ArrowLeft size={13} /> 返回网站
          </a>
        </form>
      </div>
    );
  }

  if (loading || !draft) {
    return (
      <div data-testid="admin-loader" className="min-h-screen grid place-items-center bg-slate-50">
        <div className="w-12 h-12 rounded-2xl bg-sky-400 animate-pulse" />
      </div>
    );
  }

  const set = (path, value) => {
    const next = JSON.parse(JSON.stringify(draft));
    const keys = path.split(".");
    let cur = next;
    for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
    cur[keys[keys.length - 1]] = value;
    setDraft(next);
  };

  const setArr = (arrName, idx, field, value) => {
    const next = JSON.parse(JSON.stringify(draft));
    next[arrName][idx][field] = value;
    setDraft(next);
  };

  const addArrItem = (arrName, item) => {
    const next = JSON.parse(JSON.stringify(draft));
    next[arrName].push(item);
    setDraft(next);
  };

  const removeArrItem = (arrName, idx) => {
    const next = JSON.parse(JSON.stringify(draft));
    next[arrName].splice(idx, 1);
    setDraft(next);
  };

  const updateFaq = (fn) => {
    const next = JSON.parse(JSON.stringify(draft));
    if (!next.faq) next.faq = {};
    if (!next.faq.sections) next.faq.sections = [];
    if (!next.faq.closing) next.faq.closing = {};
    if (!next.faq.disclaimer) next.faq.disclaimer = {};
    fn(next.faq);
    setDraft(next);
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const clean = { ...draft };
      delete clean._id;
      await saveContent(clean);
      await refresh();
      toast.success("内容已保存并上线 / Content saved & live");
    } catch (e) {
      if (!handle401(e)) toast.error("保存失败，请重试 / Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadPhoto(file, capZh, capEn);
      await refresh();
      setCapZh("");
      setCapEn("");
      toast.success("照片已上传 / Photo uploaded");
    } catch (err) {
      if (!handle401(err)) toast.error(err?.response?.data?.detail || "上传失败 / Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    const target = avatarTargetIdx.current;
    if (!file || !target) return;
    try {
      const r = await uploadPhoto(file, "", "", "avatar");
      setArr(target.arr, target.idx, "avatar", `/api/files/${r.storage_path}`);
      toast.success("头像已上传，点击「保存全部更改」生效 / Avatar uploaded — remember to Save");
    } catch (err) {
      if (!handle401(err)) toast.error("头像上传失败 / Avatar upload failed");
    } finally {
      if (avatarFileRef.current) avatarFileRef.current.value = "";
      avatarTargetIdx.current = null;
    }
  };

  const onDeletePhoto = async (id) => {
    try {
      await deletePhoto(id);
      await refresh();
      toast.success("照片已删除 / Photo removed");
    } catch (e) {
      if (!handle401(e)) toast.error("删除失败 / Delete failed");
    }
  };

  const onReorder = (newList) => {
    setPhotoList(newList);
    photoListRef.current = newList;
  };

  const persistOrder = async () => {
    try {
      await savePhotoOrder(photoListRef.current.map((p, i) => ({ id: p.id, order: i })));
      await refresh();
      toast.success("排序已保存 / Order saved");
    } catch (e) {
      if (!handle401(e)) toast.error("排序保存失败 / Failed to save order");
    }
  };

  const onLeadDone = async (id) => {
    try {
      await updateLead(id, "done");
      await loadLeads();
      toast.success("已标记跟进 / Marked as done");
    } catch (e) {
      if (!handle401(e)) toast.error("操作失败 / Failed");
    }
  };

  const onLeadDelete = async (id) => {
    try {
      await deleteLead(id);
      await loadLeads();
      toast.success("留言已删除 / Lead deleted");
    } catch (e) {
      if (!handle401(e)) toast.error("删除失败 / Delete failed");
    }
  };

  const biFields = (prefix, obj, base, keys, multilineKeys = []) =>
    keys.map((k) => (
      <div key={k} className="grid sm:grid-cols-2 gap-4">
        <Field label={`${FIELD_LABELS[k] || k} 中文`} testid={`${prefix}-${k}-zh`} value={obj[`${k}_zh`] || ""} multiline={multilineKeys.includes(k)}
          onChange={(v) => base ? setArr(base.arr, base.idx, `${k}_zh`, v) : set(`${prefix}.${k}_zh`, v)} />
        <Field label={`${FIELD_LABELS[k] || k} English`} testid={`${prefix}-${k}-en`} value={obj[`${k}_en`] || ""} multiline={multilineKeys.includes(k)}
          onChange={(v) => base ? setArr(base.arr, base.idx, `${k}_en`, v) : set(`${prefix}.${k}_en`, v)} />
      </div>
    ));

  return (
    <div data-testid="admin-page" className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a data-testid="admin-back-link" href="/" className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-sky-500 transition-colors duration-300">
              <ArrowLeft size={16} /> 返回网站
            </a>
            <h1 className="font-display font-extrabold text-lg text-slate-900">内容管理后台</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="admin-save-button" onClick={onSave} disabled={saving} className="rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold">
              <Save size={15} className="mr-1.5" /> {saving ? "保存中…" : "保存全部更改"}
            </Button>
            <Button data-testid="admin-logout-button" variant="ghost" size="sm" onClick={onLogout} className="text-slate-400 hover:text-red-500 rounded-full">
              <LogOut size={15} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <Tabs defaultValue="hero">
          <TabsList data-testid="admin-tabs" className="flex flex-wrap h-auto gap-1 bg-white border border-slate-200 p-1 rounded-2xl">
            {[["hero", "主视觉"], ["story", "团队故事"], ["leadership", "管理层"], ["members", "团队成员"], ["services", "服务"], ["praise", "客户表扬"], ["contact", "联系方式"], ["marquee", "跑马灯"], ["photos", "团队相册"], ["faq", "FAQ"], ["leads", "咨询留言"], ["stats", "数据统计"]].map(([v, l]) => (
              <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`} className="rounded-xl data-[state=active]:bg-sky-400 data-[state=active]:text-white font-semibold">
                {l}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="hero">
            <Card className="rounded-3xl border-slate-200">
              <CardHeader><CardTitle className="font-display">首页主视觉 Hero</CardTitle></CardHeader>
              <CardContent className="space-y-5">
                <input ref={heroFileRef} data-testid="hero-image-file-input" type="file" accept="image/*" className="hidden" onChange={onHeroImageUpload} />
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">首页大图 Hero Image</label>
                  <div className="flex items-center gap-5">
                    <div className="w-32 aspect-[4/5] rounded-2xl bg-sky-50 border border-slate-200 overflow-hidden shrink-0 grid place-items-center">
                      {imgSrc(draft.hero.image) ? (
                        <img src={imgSrc(draft.hero.image)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <ImageIcon size={24} className="text-slate-300" />
                      )}
                    </div>
                    <div className="flex-1 space-y-2">
                      <Button data-testid="hero-image-upload" variant="outline" size="sm" className="rounded-full" onClick={() => heroFileRef.current?.click()}>
                        <Upload size={14} className="mr-1.5" /> 上传新图
                      </Button>
                      <Field label="或粘贴图片 URL" testid="hero-image-url" value={draft.hero.image || ""} onChange={(v) => set("hero.image", v)} />
                    </div>
                  </div>
                </div>
                {biFields("hero", draft.hero, null, ["badge", "line1", "line2", "cta"])}
                {biFields("hero", draft.hero, null, ["subtitle"], ["subtitle"])}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="story">
            <div className="space-y-6">
              {draft.story.map((ch, i) => (
                <Card key={i} className="rounded-3xl border-slate-200" data-testid={`admin-story-item-${i}`}>
                  <CardHeader className="flex-row items-center justify-between">
                    <CardTitle className="font-display">章节 {ch.num}</CardTitle>
                    <Button data-testid={`admin-story-remove-${i}`} variant="ghost" size="sm" onClick={() => removeArrItem("story", i)} className="text-red-500 hover:text-red-600">
                      <Trash2 size={15} />
                    </Button>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    {biFields(`story-${i}`, ch, { arr: "story", idx: i }, ["title", "body"], ["body"])}
                  </CardContent>
                </Card>
              ))}
              <Button data-testid="admin-story-add" variant="outline" className="rounded-full" onClick={() => addArrItem("story", { num: `0${draft.story.length + 1}`, title_zh: "", title_en: "", body_zh: "", body_en: "" })}>
                <Plus size={15} className="mr-1" /> 添加章节
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="leadership">{peopleEditor("leadership", "管理层", true)}</TabsContent>

          <TabsContent value="members">{peopleEditor("members", "成员", true)}</TabsContent>

          <TabsContent value="services">
            <div className="space-y-6">
              {draft.services.map((svc, i) => (
                <Card key={i} className="rounded-3xl border-slate-200" data-testid={`admin-service-item-${i}`}>
                  <CardHeader className="flex-row items-center justify-between">
                    <CardTitle className="font-display">服务 {i + 1}</CardTitle>
                    <Button data-testid={`admin-service-remove-${i}`} variant="ghost" size="sm" onClick={() => removeArrItem("services", i)} className="text-red-500 hover:text-red-600">
                      <Trash2 size={15} />
                    </Button>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">图标 Icon</label>
                      <Select value={svc.icon} onValueChange={(v) => setArr("services", i, "icon", v)}>
                        <SelectTrigger data-testid={`admin-service-icon-${i}`} className="bg-white w-56"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {ICON_OPTIONS.map((ic) => <SelectItem key={ic} value={ic}>{ic}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {biFields(`service-${i}`, svc, { arr: "services", idx: i }, ["title", "desc"], ["desc"])}
                  </CardContent>
                </Card>
              ))}
              <Button data-testid="admin-service-add" variant="outline" className="rounded-full" onClick={() => addArrItem("services", { id: `svc-${Date.now()}`, icon: "Umbrella", title_zh: "", title_en: "", desc_zh: "", desc_en: "" })}>
                <Plus size={15} className="mr-1" /> 添加服务
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="praise">
            <div className="space-y-6">
              {draft.testimonials.map((tst, i) => (
                <Card key={i} className="rounded-3xl border-slate-200" data-testid={`admin-testimonial-item-${i}`}>
                  <CardHeader className="flex-row items-center justify-between">
                    <CardTitle className="font-display">表扬 {i + 1}</CardTitle>
                    <Button data-testid={`admin-testimonial-remove-${i}`} variant="ghost" size="sm" onClick={() => removeArrItem("testimonials", i)} className="text-red-500 hover:text-red-600">
                      <Trash2 size={15} />
                    </Button>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    {biFields(`testimonial-${i}`, tst, { arr: "testimonials", idx: i }, ["name", "role", "quote"], ["quote"])}
                    <Field label="头像图片 URL" testid={`testimonial-${i}-avatar`} value={tst.avatar || ""} onChange={(v) => setArr("testimonials", i, "avatar", v)} />
                  </CardContent>
                </Card>
              ))}
              <Button data-testid="admin-testimonial-add" variant="outline" className="rounded-full" onClick={() => addArrItem("testimonials", { id: `tst-${Date.now()}`, name_zh: "", name_en: "", role_zh: "", role_en: "", quote_zh: "", quote_en: "", avatar: "" })}>
                <Plus size={15} className="mr-1" /> 添加表扬
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="contact">
            <Card className="rounded-3xl border-slate-200">
              <CardHeader><CardTitle className="font-display">联系方式 Contact</CardTitle></CardHeader>
              <CardContent className="space-y-5">
                <div className="grid sm:grid-cols-3 gap-4">
                  <Field label="电话 Phone" testid="contact-phone-input" value={draft.contact.phone} onChange={(v) => set("contact.phone", v)} />
                  <Field label="邮箱 Email" testid="contact-email-input" value={draft.contact.email} onChange={(v) => set("contact.email", v)} />
                  <Field label="WhatsApp" testid="contact-whatsapp-input" value={draft.contact.whatsapp || ""} onChange={(v) => set("contact.whatsapp", v)} />
                </div>
                <Field label="留言通知邮箱（新留言自动发邮件到这里，留空则用上方邮箱）" testid="contact-notify-email-input" value={draft.contact.notify_email || ""} onChange={(v) => set("contact.notify_email", v)} />
                {biFields("contact", draft.contact, null, ["address", "hours"], ["address"])}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="marquee">
            <Card className="rounded-3xl border-slate-200">
              <CardHeader><CardTitle className="font-display">跑马灯词条（每行一条）</CardTitle></CardHeader>
              <CardContent className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">中文</label>
                  <Textarea data-testid="marquee-zh-input" rows={6} className="bg-white" value={(draft.marquee_zh || []).join("\n")}
                    onChange={(e) => set("marquee_zh", e.target.value.split("\n").filter((s) => s.trim()))} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">English</label>
                  <Textarea data-testid="marquee-en-input" rows={6} className="bg-white" value={(draft.marquee_en || []).join("\n")}
                    onChange={(e) => set("marquee_en", e.target.value.split("\n").filter((s) => s.trim()))} />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="photos">
            <div className="space-y-6">
              <Card className="rounded-3xl border-slate-200">
                <CardHeader><CardTitle className="font-display">上传新照片</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="照片说明 中文" testid="photo-caption-zh" value={capZh} onChange={setCapZh} />
                    <Field label="照片说明 English" testid="photo-caption-en" value={capEn} onChange={setCapEn} />
                  </div>
                  <label
                    data-testid="photo-upload-drop"
                    className="flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-sky-300 bg-sky-50/50 py-10 cursor-pointer hover:bg-sky-50 transition-colors duration-300"
                  >
                    <Upload size={24} className="text-sky-500" />
                    <span className="text-sm font-semibold text-slate-600">{uploading ? "上传中…" : "点击选择图片（jpg / png / webp，最大 10MB，上传后自动压缩优化）"}</span>
                    <input ref={fileRef} data-testid="photo-upload-input" type="file" accept="image/*" className="hidden" onChange={onUpload} disabled={uploading} />
                  </label>
                </CardContent>
              </Card>

              <p className="text-xs font-semibold text-slate-400 flex items-center gap-1.5">
                <GripVertical size={14} /> 拖拽卡片调整官网相册顺序
              </p>
              <Reorder.Group axis="y" as="div" values={photoList} onReorder={onReorder} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {photoList.map((p) => (
                  <Reorder.Item key={p.id} value={p} as="div" onDragEnd={persistOrder} className="cursor-grab active:cursor-grabbing">
                    <Card data-testid={`admin-photo-${p.id}`} className="rounded-3xl border-slate-200 overflow-hidden select-none">
                      <div className="aspect-[4/3] bg-slate-100 relative">
                        {photoSrc(p) ? (
                          <img src={photoSrc(p)} alt={p.caption_zh} draggable={false} className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
                        ) : (
                          <div className="absolute inset-0 grid place-items-center text-slate-300"><ImageIcon size={32} /></div>
                        )}
                      </div>
                      <CardContent className="p-4 flex items-center justify-between gap-3">
                        <p className="text-sm font-semibold text-slate-700 truncate">{p.caption_zh || p.caption_en || "（无说明）"}</p>
                        <Button data-testid={`admin-photo-delete-${p.id}`} variant="ghost" size="sm"
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={() => onDeletePhoto(p.id)} className="text-red-500 hover:text-red-600 shrink-0">
                          <Trash2 size={15} />
                        </Button>
                      </CardContent>
                    </Card>
                  </Reorder.Item>
                ))}
              </Reorder.Group>
            </div>
          </TabsContent>

          <TabsContent value="leads">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-500 flex items-center gap-2">
                  <Inbox size={16} className="text-sky-500" /> 共 {leads.length} 条留言
                </p>
                <Button data-testid="leads-refresh-button" variant="outline" size="sm" className="rounded-full" onClick={loadLeads}>刷新</Button>
              </div>
              {leads.length === 0 && (
                <Card className="rounded-3xl border-slate-200">
                  <CardContent className="py-16 text-center text-sm text-slate-400">暂无留言，官网联系区提交后会出现在这里</CardContent>
                </Card>
              )}
              {leads.map((lead) => (
                <Card key={lead.id} data-testid={`lead-item-${lead.id}`} className="rounded-3xl border-slate-200">
                  <CardContent className="p-6 flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <p className="font-display font-bold text-slate-900">{lead.name}</p>
                        <a href={`tel:${lead.phone}`} className="text-sm font-semibold text-sky-600">{lead.phone}</a>
                        <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${lead.status === "done" ? "bg-emerald-50 text-emerald-600" : "bg-orange-50 text-orange-600"}`}>
                          {lead.status === "done" ? "已跟进" : "待跟进"}
                        </span>
                      </div>
                      {lead.message && <p className="mt-2 text-sm text-slate-600">{lead.message}</p>}
                      <p className="mt-2 text-xs text-slate-400">{lead.created_at ? new Date(lead.created_at).toLocaleString("zh-CN") : ""}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {lead.status !== "done" && (
                        <Button data-testid={`lead-done-${lead.id}`} variant="outline" size="sm" className="rounded-full text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={() => onLeadDone(lead.id)}>
                          <CheckCircle2 size={14} className="mr-1" /> 标记已跟进
                        </Button>
                      )}
                      <Button data-testid={`lead-delete-${lead.id}`} variant="ghost" size="sm" className="text-red-500 hover:text-red-600" onClick={() => onLeadDelete(lead.id)}>
                        <Trash2 size={15} />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="faq">
            <FaqEditor draft={draft} updateFaq={updateFaq} />
          </TabsContent>

          <TabsContent value="stats">
            {stats ? (
              <div data-testid="stats-panel" className="space-y-6">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    ["今日访问", stats.today_views, "text-sky-500", "stats-today-views"],
                    ["累计访问", stats.total_views, "text-sky-500", "stats-total-views"],
                    ["待跟进留言", stats.new_leads, "text-orange-500", "stats-new-leads"],
                    ["累计留言", stats.total_leads, "text-orange-500", "stats-total-leads"],
                  ].map(([label, value, color, testid]) => (
                    <Card key={testid} className="rounded-3xl border-slate-200">
                      <CardContent className="p-6">
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</p>
                        <p data-testid={testid} className={`mt-2 font-display font-black text-4xl ${color}`}>{value}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <Card className="rounded-3xl border-slate-200">
                  <CardHeader className="flex-row items-center justify-between">
                    <CardTitle className="font-display">近 14 天趋势</CardTitle>
                    <Button data-testid="stats-refresh-button" variant="outline" size="sm" className="rounded-full" onClick={loadStats}>刷新</Button>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={320}>
                      <BarChart data={stats.series} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                        <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                        <Tooltip cursor={{ fill: "#f1f5f9" }} />
                        <Legend />
                        <Bar data-testid="stats-chart-views" dataKey="views" name="访问量" fill="#38BDF8" radius={[6, 6, 0, 0]} />
                        <Bar data-testid="stats-chart-leads" dataKey="leads" name="留言数" fill="#F97316" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="py-16 grid place-items-center">
                <div className="w-10 h-10 rounded-2xl bg-sky-400 animate-pulse" />
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
